import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  DerivativesOpenedPositionProjection,
  DerivativesPositionSide, // DerivativesPriceType,
  UpdateDerivativesPositionRequest,
} from 'common/api/autogenerated/trading-gateway';

import { Button, InputRange, LocalizedNumberInput, Switcher, TemporarilyUnavailableError } from 'common/components';
import { FormFieldInfo } from 'common/components/FormFieldInfo/FormFieldInfo.component';

import { localizeNumber } from 'common/helpers';
import {
  useDerivatives,
  useDerivativesOrders,
  useDerivativesPositions,
  useI18n,
  usePopup,
  useTracking,
} from 'common/hooks';
import { notify } from 'common/hooks/useNotification';
import { ModalAction } from 'common/redux/modal/initialState';

import { ServerError } from 'components/ServerError';

import { DerivativeDashboardContext } from 'views/derivatives/dashboard/DerivativeDashboard.provider';
import { StopLossField } from 'views/derivatives/dashboard/components/OrderForm/FormSettings/TakeProfitStopLoss/StopLossField.component';
import { TakeProfitField } from 'views/derivatives/dashboard/components/OrderForm/FormSettings/TakeProfitStopLoss/TakeProfitField.component';
import { DerivativesPriceType } from 'views/derivatives/dashboard/components/OrderForm/types';
import { getDerivativeFormConfiguration, putDerivativePosition } from 'views/derivatives/redux/derivatives.api';

import './index.css';

export interface FuturePositionAddTPSLPopupForm {
  tpsl: boolean;
  partial: boolean;
  partialTemp: boolean; // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
  percentage: string;
  takeProfit: number | undefined;
  stopLoss: number | undefined;
  takeProfitTriggerPriceType: DerivativesPriceType;
  stopLossTriggerPriceType: DerivativesPriceType;
  targetPrice: number | undefined;
  quantity: number | undefined;
  tradingPairCode: string;
  action: DerivativesPositionSide;
  takerFeeRate: number;
}

export interface FuturePositionAddTPSLPopupProps extends ModalAction {
  position: DerivativesOpenedPositionProjection;
  update?: Partial<FuturePositionAddTPSLPopupForm>;
}

export const FuturePositionAddTPSLPopup: React.FC<React.PropsWithChildren<FuturePositionAddTPSLPopupProps>> = ({
  position,
  update: updateFromProps,
}) => {
  const { translate } = useI18n();
  const { hide, submit, update } = usePopup();
  const [isDeleting, setIsDeleting] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [bubbleValue, setBubbleValue] = useState<number | null>(null);

  const { positions } = useDerivativesPositions();

  // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
  const { track } = useTracking();

  const positionFromStore = useMemo(() => positions.find((x) => x.id === position.id), [position.id, positions]);

  const isEditingPositionTakeProfitStopLoss = useMemo(
    () => !!position.takeProfitPrice || !!position.stopLossPrice,
    [position.stopLossPrice, position.takeProfitPrice],
  );

  const {
    stream: { price, baseCurrency = '', quoteCurrency, decimalPlaces = 1, markPrice },
    info: { digitsForBase },
  } = useDerivatives(position.tradingPairCode);

  const {
    info: { minQuantity },
  } = useContext(DerivativeDashboardContext);

  const { orders: ordersWithEntireSize } = useDerivativesOrders(
    ({ contracts, openedPositionId }) => openedPositionId === position.id && !contracts,
  );

  const isEntireOrderAlreadyExist = useMemo(() => !!ordersWithEntireSize.length, [ordersWithEntireSize.length]);

  // const isPartialOrder = useMemo(() => !!order?.quantity && order?.quantity !== position.quantity, [
  //   order?.quantity,
  //   position.quantity,
  // ]);

  const isDirtyFromProps = useMemo(() => updateFromProps && !!Object.keys(updateFromProps).length, [updateFromProps]);

  const methods = useForm<FuturePositionAddTPSLPopupForm>({
    mode: 'onChange',
    defaultValues: {
      partial: false, // isEditingPositionOrder ? isPartialOrder : isEntireOrderAlreadyExist,
      takeProfit: position.takeProfitPrice || undefined,
      stopLoss: position.stopLossPrice || undefined,
      takeProfitTriggerPriceType: DerivativesPriceType.LAST, // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
      stopLossTriggerPriceType: DerivativesPriceType.LAST, // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
      tradingPairCode: position.tradingPairCode || undefined,
      action: position.side || DerivativesPositionSide.BUY,
      targetPrice: position.entryPrice,
      quantity: position.quantity || undefined,
      ...updateFromProps,
    },
  });

  const {
    takeProfit,
    stopLoss,
    takeProfitTriggerPriceType,
    stopLossTriggerPriceType,
    partial,
    partialTemp, // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
    quantity,
    // percentage,
  } = methods.watch();

  useEffect(
    function requestTradingPairConfiguration() {
      if (position.tradingPairCode)
        getDerivativeFormConfiguration(position.tradingPairCode).then((response) => {
          methods.setValue('takerFeeRate', response.takerFeeRate ?? 0);
        });
    },
    [methods, position.tradingPairCode],
  );

  const isEntirePositionSize = useMemo(
    () => Number(quantity) >= Number(position.quantity) || !partial,
    [partial, position.quantity, quantity],
  );

  // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
  const isQuantityFieldEnabled = useMemo(() => false, []);
  // useMemo(() => {
  //   if (isEditingPositionTakeProfitStopLoss) return !isEntirePositionSize;

  //   return isEntireOrderAlreadyExist || partial;
  // }, [isEditingPositionTakeProfitStopLoss, isEntireOrderAlreadyExist, isEntirePositionSize, partial]);

  const {
    setValue,
    formState: { isValid, isSubmitting, isDirty, errors },
    trigger,
  } = methods;

  const symbol = useMemo(() => `${baseCurrency}/${quoteCurrency}`, [baseCurrency, quoteCurrency]);

  const quantityValidationError = useMemo(() => errors.quantity?.message, [errors.quantity?.message]);

  const title = useMemo(() => {
    const tag = translate.toText(
      isEntirePositionSize || Number(quantity) === Number(position.quantity)
        ? 'mobile:derivative:position:details:order:entire'
        : 'mobile:derivative:position:details:order:partial',
    );
    return (
      <div className='derivative-position-order-edit__popup-header'>
        <span className='derivative-position-order-edit__popup-title'>
          {translate.toText(
            isEditingPositionTakeProfitStopLoss
              ? 'mobile:derivative:position:add-tpls:edit:title'
              : 'mobile:derivative:position:add-tpls:add:title',
          )}
        </span>
        <span className='derivative-position-order-edit__popup-mode'>{tag}</span>
      </div>
    );
  }, [isEditingPositionTakeProfitStopLoss, isEntirePositionSize, position.quantity, quantity, translate]);

  const takeProfitCurrentPrice = useMemo(() => {
    if (takeProfitTriggerPriceType === DerivativesPriceType.MARK) return markPrice;
    return price;
  }, [markPrice, price, takeProfitTriggerPriceType]);

  const stopLossCurrentPrice = useMemo(() => {
    if (stopLossTriggerPriceType === DerivativesPriceType.MARK) return markPrice;
    return price;
  }, [markPrice, price, stopLossTriggerPriceType]);

  const validateTakeProfitField = useMemo(
    () => ({
      required: (v: string | number | undefined) => {
        const fieldValue = Number(v);

        if (isEditingPositionTakeProfitStopLoss) return true;

        if (!stopLoss) return !!fieldValue || '';

        return true;
      },
      shouldBeMoreThenEntryPriceForLongPosition: (v: string | number | undefined) => {
        const fieldValue = Number(v);
        const actualPrice = Number(takeProfitCurrentPrice);
        const actualPriceformatted = localizeNumber(takeProfitCurrentPrice, {
          minDigits: decimalPlaces,
        });

        if (!fieldValue) return true;

        if (position.side === DerivativesPositionSide.BUY)
          return (
            fieldValue > actualPrice ||
            translate
              .toText('mobile:derivative:position:add-tpls:takeprofit:error:min')
              .replace('{price}', actualPriceformatted)
          );
        if (position.side === DerivativesPositionSide.SELL)
          return (
            fieldValue < actualPrice ||
            translate
              .toText('mobile:derivative:position:add-tpls:takeprofit:error:max')
              .replace('{price}', actualPriceformatted)
          );

        return true;
      },
    }),
    [takeProfitCurrentPrice, decimalPlaces, isEditingPositionTakeProfitStopLoss, position.side, stopLoss, translate],
  );

  const validateStopLossField = useMemo(
    () => ({
      required: (v: string | number | undefined) => {
        const fieldValue = Number(v);
        if (isEditingPositionTakeProfitStopLoss) return true;

        if (!takeProfit) return !!fieldValue || '';

        return true;
      },
      shouldBeMoreThenEntryPriceForLongPosition: (v: string | number | undefined) => {
        const fieldValue = Number(v);
        const actualPrice = Number(stopLossCurrentPrice);
        const actualPriceformatted = localizeNumber(stopLossCurrentPrice, {
          minDigits: decimalPlaces,
        });

        if (!fieldValue) return true;

        if (position.side === DerivativesPositionSide.BUY)
          return (
            fieldValue < actualPrice ||
            translate
              .toText('mobile:derivative:position:add-tpls:stoploss:error:max')
              .replace('{price}', actualPriceformatted)
          );
        if (position.side === DerivativesPositionSide.SELL)
          return (
            fieldValue > actualPrice ||
            translate
              .toText('mobile:derivative:position:add-tpls:stoploss:error:min')
              .replace('{price}', actualPriceformatted)
          );

        return true;
      },
    }),
    [stopLossCurrentPrice, decimalPlaces, isEditingPositionTakeProfitStopLoss, position.side, takeProfit, translate],
  );

  const validatePositionQuantity = useMemo(
    () => ({
      isRequired: (v: string | number | undefined) => {
        const fieldValue = Number(v);

        return !partial || !!fieldValue || '';
      },
      shouldNotBeMoreThenPositionSize: (v: string | number | undefined) => {
        const fieldValue = Number(v);

        return (
          !partial ||
          fieldValue <= Number(position.quantity) ||
          translate.toText('mobile:derivative:position:add-tpls:quantity:error:max')
        );
      },
      shouldNotBeMoreThenMinimumSize: (v: string | number | undefined) => {
        const fieldValue = Number(v);

        return (
          fieldValue >= minQuantity ||
          translate
            .toText('mobile:derivative:form:quantity:error:min')
            .replace('{min}', localizeNumber(minQuantity, { minDigits: 0, maxDigits: 6 }))
        );
      },
    }),
    [minQuantity, partial, position.quantity, translate],
  );

  const onDelete = useCallback(() => {
    if (position?.id) {
      setIsDeleting(true);
      return putDerivativePosition(position.id, {
        takeProfitPrice: null,
        stopLossPrice: null,
      })
        .then(() => {
          hide();
          notify({
            title: translate
              .toText('mobile:notifications:derivative:position:tpsl:deleted')
              .replace('{symbol}', symbol),
          });
        })
        .catch((error) => setServerError(error?.body?.message))
        .finally(() => {
          setIsDeleting(false);
        });
    }
    return null;
  }, [hide, position, symbol, translate]);

  const onChangeBalanceSlider = useCallback(
    (slider: string) => {
      setValue('quantity', +slider);
      setBubbleValue(Number(slider) / Number(positionFromStore?.quantity));
    },
    [positionFromStore?.quantity, setValue],
  );

  const onSubmit = useCallback(
    (formValues: FuturePositionAddTPSLPopupForm) => {
      const _order: UpdateDerivativesPositionRequest = {
        takeProfitPrice: formValues.takeProfit || null,
        stopLossPrice: formValues.stopLoss || null,
      };

      return putDerivativePosition(position.id, _order)
        .then(() => {
          submit();

          notify({
            title: translate
              .toText(
                isEditingPositionTakeProfitStopLoss
                  ? !formValues.takeProfit && !formValues.stopLoss
                    ? 'mobile:notifications:derivative:position:tpsl:deleted'
                    : 'mobile:notifications:derivative:position:tpsl:updated'
                  : 'mobile:notifications:derivative:position:tpsl:created',
              )
              .replace('{symbol}', symbol),
          });
        })
        .catch((error) => setServerError(error?.body?.message));
    },
    [position.id, submit, translate, isEditingPositionTakeProfitStopLoss, symbol],
  );

  useEffect(
    function updateQuantityOnPositionSizeUpdated() {
      if (bubbleValue) setValue('quantity', Number(positionFromStore?.quantity) * bubbleValue);
    },
    [bubbleValue, position.quantity, positionFromStore?.quantity, setValue],
  );

  useEffect(
    function updateModalTag() {
      update({ title });
    },
    [title, update],
  );

  useEffect(
    function validateOnCurrentPriceChanged() {
      if (takeProfitCurrentPrice) trigger('takeProfit');
      if (stopLossCurrentPrice) trigger('stopLoss');
    },
    [stopLossCurrentPrice, takeProfitCurrentPrice, trigger],
  );

  const balanceRangeStep = useMemo(() => Number(position?.quantity) / 100, [position?.quantity]);

  return (
    <FormProvider {...methods}>
      <form className='derivative-position-order-edit' onSubmit={methods.handleSubmit(onSubmit)}>
        <section className='derivative-position-order-edit__section mb-8'>
          <TakeProfitField
            currency={quoteCurrency}
            digits={decimalPlaces}
            validate={validateTakeProfitField}
            autoFocus
          />
        </section>

        <section className='derivative-position-order-edit__section'>
          <StopLossField currency={quoteCurrency} digits={decimalPlaces} validate={validateStopLossField} />
        </section>

        {!isEditingPositionTakeProfitStopLoss && !isEntireOrderAlreadyExist && (
          <FormFieldInfo className='derivative-position-order-edit__position-switcher'>
            <FormFieldInfo.Label
              tooltipPlacement='topLeft'
              tooltip={translate.toText('mobile:derivative:position:add-tpls:partial:tooltip')}
            >
              <span className='main-txt-color'>
                {' '}
                {translate.toText('mobile:derivative:position:add-tpls:partial:label')}
              </span>
            </FormFieldInfo.Label>
            <FormFieldInfo.Value>
              {/* <Switcher
                name='partial'
                checked={partial}
                disabled={isEntireOrderAlreadyExist}
                className='derivative__order-form__tpsl'
                dataAttribute='derivative-position-tpsl-partial-swither'
              /> */}

              {/* // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR */}
              <Switcher
                name='partialTemp'
                checked={!!partialTemp}
                onChange={(value) => {
                  track('futures:temp-unavailable:chosen:tpsl-partial');
                  setValue('partialTemp', !!value);
                }}
              />
            </FormFieldInfo.Value>
          </FormFieldInfo>
        )}

        {isQuantityFieldEnabled && (
          <section className='derivative-position-order-edit__section mt-8'>
            <LocalizedNumberInput
              name='quantity'
              value={quantity}
              label={translate.toText('mobile:derivative:form:quantity:label')}
              currency={baseCurrency}
              digits={digitsForBase}
              error={quantityValidationError}
              validate={validatePositionQuantity}
              deps={['partial']}
              autoFocus
            />
            <FormFieldInfo className='derivative-position-order-edit__current'>
              <FormFieldInfo.Label>
                {translate.toText('mobile:derivative:position:add-tpls:current:label')}
              </FormFieldInfo.Label>
              <FormFieldInfo.Value>
                {positionFromStore?.quantity} {baseCurrency}
              </FormFieldInfo.Value>
            </FormFieldInfo>
            <InputRange
              value={Number(quantity)}
              maxValue={Number(position.quantity)}
              step={balanceRangeStep}
              onChange={onChangeBalanceSlider}
            />
          </section>
        )}

        {serverError && <ServerError error={serverError} className='derivative-position-order-edit__server-error' />}
        {partialTemp && <TemporarilyUnavailableError className='text-center w-full' />}

        <div className='derivative-position-order-edit__controls'>
          {isEditingPositionTakeProfitStopLoss && (
            <Button deleted loading={isDeleting} disabled={isDeleting || isSubmitting} large stretch onClick={onDelete}>
              {translate.toText('mobile:derivative:position:add-tpls:delete-button')}
            </Button>
          )}
          <Button
            type='submit'
            disabled={!isValid || (!isDirty && !isDirtyFromProps) || isDeleting}
            loading={isSubmitting}
            large
            stretch={isEditingPositionTakeProfitStopLoss}
          >
            {translate.toText('mobile:derivative:position:add-tpls:submit-button')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
